import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { Plus, Shuffle, Timer, Target, X } from 'lucide-react';
import { AutomationNodeType, AutomationActionNodeSubType } from '@frontend/app/types/globalTypes';
import { Button } from '@/shadcn/components/ui/button';
import { cn } from '@/shadcn/lib/utils';
import { FlowNodeData, NodeTemplate, NodeCategory } from '../types';

interface PlaceholderNodeV2Props {
  data: FlowNodeData;
}

export const PlaceholderNodeV2: React.FC<PlaceholderNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    // isEditing,
    isSelected,
    onNodeClick,
    onDeleteNode,
    // onAddChild,
  } = data;

  const [showOptions, setShowOptions] = useState(false);

  // Get placeholder context from metadata
  const placeholderType = metadata?.placeholderType;

  // Determine display text based on context
  const getDisplayText = () => {
    switch (placeholderType) {
      case 'else':
        return {
          title: 'Select Block',
          description: 'Creators who don\'t meet the conditions above will follow this path.',
        };
      case 'then':
        return {
          title: 'Select Block',
          description: 'Creators who meet the conditions above will follow this path.',
        };
      case 'path':
        return {
          title: 'Select Block',
          description: 'Paths will be executed in parallel.',
        };
      default:
        return {
          title: 'Select Block',
          description: 'Choose a block type to continue',
        };
    }
  };

  const displayText = getDisplayText();

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
    setShowOptions(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Call the delete handler to remove this placeholder node
    onDeleteNode?.(nodeId);
  };

  const handleReplaceWithNode = (template: NodeTemplate) => {
    if (data.onReplacePlaceholder) {
      // Ensure proper default metadata based on node type
      let defaultMetadata = { ...template.defaultMetadata };

      if (template.type === 'ACTION') {
        if (template.defaultMetadata?.isActionPlaceholder) {
          // Keep the action placeholder metadata
          defaultMetadata = {
            ...defaultMetadata,
            isActionPlaceholder: true,
          };
        } else {
          // For specific action types
          defaultMetadata = {
            ...defaultMetadata,
            resourceId: null, // Default resource ID
            fromPreviousSender: false,
            templateId: template.subType === AutomationActionNodeSubType.SEND_EMAIL ? 1 : undefined,
          };
        }
      } else if (template.type === 'CONDITION') {
        defaultMetadata = {
          ...defaultMetadata,
          condition: null // Start with empty condition - user must configure
        };
      } else if (template.type === 'DELAY') {
        defaultMetadata = {
          ...defaultMetadata,
          durationMs: 3600000, // Default 1 hour
        };
      }

      const newNode = {
        id: nodeId, // Keep the same ID to preserve position
        type: template.type,
        subType: template.subType,
        children: [],
        elseChildren: template.type === 'CONDITION' ? [] : undefined,
        metadata: defaultMetadata,
        variables: [],
      };

      data.onReplacePlaceholder(nodeId, newNode);
    }
    setShowOptions(false);
  };

  const nodeOptions = [
    {
      type: AutomationNodeType.CONDITION,
      category: NodeCategory.LOGIC,
      displayName: 'Condition',
      description: 'Check a condition',
      icon: 'shuffle',
      defaultMetadata: {},
      allowsChildren: true,
      maxChildren: 2,
    },
    {
      type: AutomationNodeType.DELAY,
      category: NodeCategory.UTILITY,
      displayName: 'Delay',
      description: 'Wait for a specified time',
      icon: 'timer',
      defaultMetadata: {}, // No default values - user must configure
      allowsChildren: true,
    },
    {
      type: AutomationNodeType.ACTION,
      subType: undefined,
      category: NodeCategory.ACTION,
      displayName: 'Action',
      description: 'Perform an action',
      icon: 'target',
      defaultMetadata: { isActionPlaceholder: true },
      allowsChildren: false,
    },
  ];

  // Helper function to get icon component from string
  const getIconComponent = (iconName: string) => {
    switch (iconName) {
      case 'shuffle':
        return <Shuffle className="h-4 w-4" />;
      case 'timer':
        return <Timer className="h-4 w-4" />;
      case 'target':
        return <Target className="h-4 w-4" />;
      default:
        return <Target className="h-4 w-4" />;
    }
  };

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main placeholder container */}
      <div className="flex flex-col items-center justify-center relative w-[342px] transition-all duration-200">
        <div className={cn(
          'w-full bg-white border-2 border-dashed border-gray-300 rounded-2xl transition-all duration-200 min-h-[120px] flex flex-col items-center justify-center',
          isSelected && 'border-black',
          showOptions && 'border-grey-400'
        )}
        >
          {!showOptions ? (
            // Initial placeholder state
            <div className="relative w-full">
              <div
                className="flex flex-col items-center justify-center gap-3 p-6 cursor-pointer hover:bg-grey-1 rounded-2xl transition-colors w-full bg-grey-2"
                onClick={handleNodeClick}
              >
                <div className="rounded-full flex items-center justify-center">
                  <Plus className="h-6 w-6 text-grey-5" />
                </div>
                <div className="text-center">
                  <div className="text-sm font-medium text-grey-5 mb-1">{displayText.title}</div>
                  <div className="text-xs text-grey-5">
                    {displayText.description}
                  </div>
                </div>
              </div>
              {/* Close button */}
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 p-1 h-6 w-6 hover:bg-grey-3 rounded-md"
                onClick={handleClose}
              >
                <X className="h-3 w-3 text-grey-5" />
              </Button>
            </div>
          ) : (
            <div className="p-4 w-full relative">
              <div className="text-center mb-4">
                <div className="text-sm font-medium text-grey-5 mb-1">{displayText.title}</div>
              </div>

              <div className="flex justify-center gap-2">
                {nodeOptions.map((option) => (
                  <Button
                    key={`${option.type}-${option.subType || 'default'}`}
                    variant="outline"
                    size="sm"
                    className="flex flex-col items-center gap-2 px-2 py-2 h-auto min-w-[75px] hover:bg-grey-1"
                    onClick={() => handleReplaceWithNode(option)}
                  >
                    {getIconComponent(option.icon)}
                    <span className="text-xs font-medium">{option.displayName}</span>
                  </Button>
                ))}
              </div>

              {/* Close button in options view */}
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-2 right-2 p-1 h-6 w-6 hover:bg-grey-3 rounded-md"
                onClick={handleClose}
              >
                <X className="h-3 w-3 text-grey-5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />
    </div>
  );
};
